import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Button } from "../components/ui/button";
import { useBooking } from "../context/BookingContext";
import api from "../lib/api";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-44 pb-24 text-center" data-testid="blog-not-found">
        <p className="text-[#5C6664]">Article not found.</p>
        <Link to="/blog" className="text-iri-mid underline mt-3 inline-block">Back to journal</Link>
      </div>
    );
  }
  if (!post) return <div className="pt-44 pb-24 text-center text-[#8A9492]">Loading…</div>;

  return (
    <article data-testid="blog-detail-page">
      <div className="pt-32 md:pt-40 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#5C6664] hover:text-ink mb-8" data-testid="back-to-blog">
            <ArrowLeft className="h-4 w-4" /> Journal
          </Link>
          <Reveal>
            <span className="text-xs uppercase tracking-[0.2em] text-[#8A9492]">
              {post.author} · {post.date} · {post.read_time}
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-light tracking-tight text-ink leading-tight">
              {post.title}
            </h1>
          </Reveal>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-12 mt-10">
          <Reveal delay={0.1}>
            <div className="img-zoom rounded-[2rem] overflow-hidden aspect-[16/9]">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-12 mt-12">
          <Reveal delay={0.15}>
            <div className="prose prose-lg max-w-none">
              {post.content.split("\n\n").map((para, i) => (
                <p key={i} className="text-[#5C6664] leading-relaxed text-lg font-light mb-6">
                  {para.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
                    seg.startsWith("**") && seg.endsWith("**")
                      ? <strong key={j} className="text-ink font-medium">{seg.slice(2, -2)}</strong>
                      : seg
                  )}
                </p>
              ))}
            </div>

            {post.disclaimer && (
              <p className="text-xs text-[#8A9492] leading-relaxed mt-8 border-t border-slate-200 pt-6" data-testid="blog-disclaimer">
                {post.disclaimer}
              </p>
            )}

            <div className="mt-12 iri-gradient rounded-3xl p-8 md:p-10 text-center">
              <h3 className="font-heading text-2xl font-light text-ink">Ready to experience it yourself?</h3>
              <Button
                onClick={() => openBooking()}
                data-testid="blog-book-button"
                className="mt-5 bg-ink text-white rounded-full h-12 px-8 hover:bg-ink/90"
              >
                Book an appointment
              </Button>
            </div>
          </Reveal>
        </div>
      </div>
    </article>
  );
}
